import React, { useEffect, useState, Children, Component } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Menu, X, Heart, Users, Compass, ArrowRight, CheckCircle2, TrendingUp, Target, Mail, ChevronRight, ArrowUpRight, Search, Sparkles, Rocket } from 'lucide-react';
// --- Animation Variants ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 60
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};
const scaleOnHover = {
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.3
    }
  }
};
// --- Logo Component ---
const KatalystLogo = ({
  isDark = false,
  className = ''
}: {
  isDark?: boolean;
  className?: string;
}) => {
  const logoUrl = 'https://uploadthingy.s3.us-west-1.amazonaws.com/ghvC1DW88MLqr9B3Sht7tV/Katalyst_Product_Lab_Logo.png';
  return <motion.img src={logoUrl} alt="Katalyst Product Lab" className={`${className}`} initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.5,
    ease: 'easeOut'
  }} data-id="element-0" />;
};
// --- Components ---
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Expertise',
    href: '#expertise'
  }, {
    name: 'Method',
    href: '#method'
  }, {
    name: 'Philosophy',
    href: '#philosophy'
  }];
  return <motion.nav initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.6,
    ease: 'circOut'
  }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'}`} data-id="element-1">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-2">
        <div className="flex justify-between items-center" data-id="element-3">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 group" data-id="element-4">
            <KatalystLogo isDark={!isScrolled} className="h-10 w-auto transition-all duration-300 group-hover:scale-110" data-id="element-5" />
            <div className="flex flex-col" data-id="element-6">
              <span className={`text-lg font-bold leading-none tracking-tight ${isScrolled ? 'text-brand-dark' : 'text-white'}`} data-id="element-7">
                Katalyst
              </span>
              <span className={`text-[10px] font-bold tracking-[0.2em] uppercase ${isScrolled ? 'text-brand-dark/60' : 'text-white/70'}`} data-id="element-8">
                Product Lab
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8" data-id="element-9">
            {navLinks.map(link => <a key={link.name} href={link.href} className={`text-sm font-medium transition-colors hover:text-brand-accent ${isScrolled ? 'text-gray-600' : 'text-white/90'}`} data-id="element-10">
                {link.name}
              </a>)}
            <motion.a href="#contact" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${isScrolled ? 'bg-brand-dark text-white hover:bg-brand-accent' : 'bg-white text-brand-dark hover:bg-brand-accent hover:text-white'}`} data-id="element-11">
              Let's Connect
            </motion.a>
          </div>

          {/* Mobile Toggle */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`md:hidden p-2 ${isScrolled ? 'text-brand-dark' : 'text-white'}`} data-id="element-12">
            {isMobileMenuOpen ? <X data-id="element-13" /> : <Menu data-id="element-14" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-15">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-b border-gray-100 overflow-hidden" data-id="element-16">
            <div className="px-6 py-8 space-y-4 flex flex-col" data-id="element-17">
              {navLinks.map(link => <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-gray-800 hover:text-brand-accent" data-id="element-18">
                  {link.name}
                </a>)}
              <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="mt-4 px-6 py-3 bg-brand-dark text-white text-center rounded-lg font-semibold" data-id="element-19">
                Let's Connect
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.nav>;
};
const Hero = () => {
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-brand-dark via-indigo-900 to-brand-dark" data-id="element-20">
      {/* Animated background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" data-id="element-21" />
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-brand-accent/20 rounded-full blur-[120px] animate-pulse" data-id="element-22" />
      <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-brand-gold/20 rounded-full blur-[120px] animate-pulse" style={{
      animationDelay: '1s'
    }} data-id="element-23" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 text-center" data-id="element-24">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-25">
          <motion.h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} data-id="element-26">
            Transform Ideas Into <br data-id="element-27" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-accent via-teal-300 to-brand-gold" data-id="element-28">
              Products That Thrive
            </span>
          </motion.h1>

          <motion.p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto font-light leading-relaxed" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4,
          duration: 0.8
        }} data-id="element-29">
            Product consulting for founders, nonprofits, and mission-driven
            teams. We help you go from raw idea to working product through
            scrappy prototyping, fast user research, and vibe-coded web builds.
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row gap-4 justify-center items-center" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.8
        }} data-id="element-30">
            <motion.a href="#contact" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="px-8 py-4 bg-white text-brand-dark rounded-full font-bold text-lg hover:bg-brand-accent hover:text-white transition-all shadow-xl hover:shadow-2xl flex items-center gap-2 group" data-id="element-31">
              Start Building Together
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" data-id="element-32" />
            </motion.a>
            <motion.a href="#expertise" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all border border-white/20" data-id="element-33">
              See How We Work
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2,
        duration: 1
      }} className="absolute bottom-12 left-1/2 transform -translate-x-1/2" data-id="element-34">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2" data-id="element-35">
            <motion.div animate={{
            y: [0, 12, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-36" />
          </motion.div>
        </motion.div>
      </div>
    </section>;
};
const ExpertiseCard = ({
  icon: Icon,
  title,
  problem,
  solution,
  tags,
  delay
}: any) => {
  return <motion.div variants={{
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay,
        duration: 0.6
      }
    }
  }} whileHover={{
    y: -10
  }} className="group relative bg-white p-8 rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden h-full flex flex-col" data-id="element-37">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-dark via-brand-accent to-brand-gold transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" data-id="element-38" />

      <div className="mb-8 relative" data-id="element-39">
        <div className="w-16 h-16 rounded-2xl bg-brand-surface flex items-center justify-center group-hover:bg-brand-dark transition-colors duration-500" data-id="element-40">
          <Icon className="w-8 h-8 text-brand-dark group-hover:text-brand-accent transition-colors duration-500" data-id="element-41" />
        </div>
      </div>

      <h3 className="text-2xl font-bold text-brand-dark mb-4 group-hover:text-brand-accent transition-colors" data-id="element-42">
        {title}
      </h3>

      <div className="space-y-4 mb-8 flex-grow" data-id="element-43">
        <div className="p-4 rounded-xl bg-amber-50 border-l-4 border-brand-gold" data-id="element-44">
          <div className="flex items-center gap-2 mb-2" data-id="element-45">
            <span className="px-2.5 py-0.5 bg-brand-gold text-white text-xs font-bold uppercase tracking-wider rounded" data-id="element-46">
              The Challenge
            </span>
          </div>
          <p className="text-gray-700 leading-relaxed" data-id="element-47">{problem}</p>
        </div>
        <div className="p-4 rounded-xl bg-brand-accent/5 border-l-4 border-brand-accent" data-id="element-48">
          <div className="flex items-center gap-2 mb-2" data-id="element-49">
            <span className="px-2.5 py-0.5 bg-brand-accent text-white text-xs font-bold uppercase tracking-wider rounded" data-id="element-50">
              How We Help
            </span>
          </div>
          <p className="text-gray-700 leading-relaxed" data-id="element-51">{solution}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-auto" data-id="element-52">
        {tags.map((tag: string) => <span key={tag} className="px-3 py-1 bg-gray-50 text-gray-500 text-xs font-medium rounded-full border border-gray-100" data-id="element-53">
            {tag}
          </span>)}
      </div>
    </motion.div>;
};
const Expertise = () => {
  return <section id="expertise" className="py-32 bg-brand-surface relative" data-id="element-54">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-55">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="mb-20 max-w-3xl" data-id="element-56">
          <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-6" data-id="element-57">
            From Idea to <br data-id="element-58" />
            <span className="text-brand-accent" data-id="element-59">Working Product</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed" data-id="element-60">
            We help you build your first product fast. Whether you're a founder
            with an idea, a nonprofit with a mission, or a team ready to ship
            something new—we're here to make it real.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid md:grid-cols-3 gap-8" data-id="element-61">
          <ExpertiseCard icon={TrendingUp} title="Product Ideation" problem="You have a vision but aren't sure what to build first or how to validate it." solution="We help you clarify your idea, identify your core users, and define what success looks like. Then we map out a scrappy first version you can actually ship." tags={['Product Strategy', 'User Research', 'MVP Planning']} delay={0} data-id="element-62" />
          <ExpertiseCard icon={Target} title="Scrappy Prototyping" problem="You need to test your idea quickly without spending months or big budgets." solution="We build fast, functional prototypes you can put in front of real users. Think vibe-coded web builds, clickable demos, and working MVPs—not polished perfection." tags={['Rapid Prototyping', 'Web Development', 'User Testing']} delay={0.2} data-id="element-63" />
          <ExpertiseCard icon={Users} title="Fast User & Market Analysis" problem="You're not sure if people actually want what you're building or how to reach them." solution="We run quick user interviews, analyze your market, and help you understand who your early adopters are and what they need. No six-month research projects—just fast, actionable insights." tags={['User Interviews', 'Market Research', 'Go-to-Market']} delay={0.4} data-id="element-64" />
        </motion.div>
      </div>
    </section>;
};
const Philosophy = () => {
  return <section id="philosophy" className="pt-12 pb-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden" data-id="element-65">
      {/* Decorative elements */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-brand-accent/10 rounded-full blur-3xl" data-id="element-66" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-brand-gold/10 rounded-full blur-3xl" data-id="element-67" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10" data-id="element-68">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center mb-20" data-id="element-69">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-brand-dark" data-id="element-70">
            What{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-accent to-brand-gold" data-id="element-71">
              Drives Us
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-72">
            We believe the best products come from deeply understanding people
            and moving fast. Our approach combines genuine curiosity about your
            users with a bias toward shipping real, working things.
          </p>
        </motion.div>

        {/* Stacked Large Cards with Side Icons */}
        <div className="space-y-8 max-w-5xl mx-auto" data-id="element-73">
          {/* Deep Discovery */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex gap-6 items-start" data-id="element-74">
            <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-brand-dark to-indigo-900 flex items-center justify-center shadow-lg" data-id="element-75">
              <Search className="w-10 h-10 text-brand-accent" data-id="element-76" />
            </div>
            <div className="flex-grow bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-77">
              <h3 className="text-3xl font-bold text-brand-dark mb-3" data-id="element-78">
                Deep Discovery
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4" data-id="element-79">
                We don't rush to solutions. We immerse ourselves in
                understanding the real challenges your users face—their
                frustrations, their workflows, their unspoken needs. Only when
                we truly understand the problem can we create something
                transformative.
              </p>
              <div className="inline-flex items-center gap-2 px-5 py-3 bg-brand-dark text-white rounded-lg shadow-sm" data-id="element-80">
                <span className="text-sm font-bold" data-id="element-81">
                  Deep curiosity drives breakthrough innovation
                </span>
              </div>
            </div>
          </motion.div>

          {/* See the Human */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="flex gap-6 items-start flex-row-reverse" data-id="element-82">
            <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-brand-accent to-cyan-500 flex items-center justify-center shadow-lg" data-id="element-83">
              <Users className="w-10 h-10 text-white" data-id="element-84" />
            </div>
            <div className="flex-grow bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-right" data-id="element-85">
              <h3 className="text-3xl font-bold text-brand-dark mb-3" data-id="element-86">
                See the Human
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4" data-id="element-87">
                Data tells us what's happening. Empathy tells us why. We see the
                human stories behind conversion rates and engagement numbers.
                Every decision we make considers the real people whose lives
                your product touches.
              </p>
              <div className="inline-flex items-center gap-2 px-5 py-3 bg-brand-accent text-white rounded-lg shadow-sm" data-id="element-88">
                <span className="text-sm font-bold" data-id="element-89">
                  Empathy transforms good products into beloved ones
                </span>
              </div>
            </div>
          </motion.div>

          {/* Build with Heart */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="flex gap-6 items-start" data-id="element-90">
            <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-600 to-amber-600 flex items-center justify-center shadow-lg" data-id="element-91">
              <Heart className="w-10 h-10 text-yellow-200" data-id="element-92" />
            </div>
            <div className="flex-grow bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-93">
              <h3 className="text-3xl font-bold text-brand-dark mb-3" data-id="element-94">
                Build with Heart
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4" data-id="element-95">
                Great products aren't just well-engineered—they're built with
                care, honesty, and respect for users. We believe in transparent
                processes, ethical design, and creating value that genuinely
                improves people's lives, not just business metrics.
              </p>
              <div className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-lg shadow-sm" data-id="element-96">
                <span className="text-sm font-bold" data-id="element-97">
                  Purpose and profit grow together
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Quote - Teal Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="mt-32" data-id="element-98">
          <div className="bg-gradient-to-br from-brand-accent to-cyan-600 py-20 px-8 md:px-16 rounded-3xl shadow-2xl relative overflow-hidden" data-id="element-99">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" data-id="element-100" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-dark/10 rounded-full blur-3xl" data-id="element-101" />

            <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-102">
              <div className="h-px w-24 bg-white/30 mx-auto mb-8" data-id="element-103" />
              <blockquote className="text-3xl md:text-4xl font-light text-white leading-relaxed italic mb-8" data-id="element-104">
                "The greatest products speak for themselves; our work ensures
                they have something truly exceptional to say."
              </blockquote>
              <p className="text-yellow-200 font-bold text-lg" data-id="element-105">
                — The Katalyst Philosophy
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};
const Method = () => {
  return <section id="method" className="py-32 pb-12 bg-gradient-to-b from-white via-brand-surface to-white relative overflow-hidden" data-id="element-106">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative" data-id="element-107">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="mb-24 text-center" data-id="element-108">
          <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-6" data-id="element-109">
            Our <span className="text-brand-accent" data-id="element-110">Approach</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto" data-id="element-111">
            Whether you're a founder building your first prototype, a nonprofit
            developing an app for your community, or creating a personal
            project—we follow a simple, collaborative process to bring your idea
            to life.
          </p>
        </motion.div>

        {/* Timeline Container */}
        <div className="relative" data-id="element-112">
          {/* Vertical Timeline Line - Centered */}
          <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-dark via-brand-accent to-brand-gold hidden md:block" data-id="element-113" />

          {/* Step 1: Discovery & Alignment - Right Side */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="relative pb-24 grid md:grid-cols-2 gap-8 items-center" data-id="element-114">
            {/* Left spacer */}
            <div className="hidden md:block" data-id="element-115" />

            {/* Content Card - Right */}
            <div className="relative" data-id="element-116">
              <div className="bg-gradient-to-br from-brand-dark to-indigo-900 text-white p-8 md:p-10 rounded-3xl shadow-xl relative overflow-hidden" data-id="element-117">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent/10 rounded-full blur-3xl" data-id="element-118" />
                <div className="relative z-10" data-id="element-119">
                  <div className="flex items-center gap-3 mb-4" data-id="element-120">
                    <Search className="w-8 h-8 text-brand-accent" data-id="element-121" />
                    <div data-id="element-122">
                      <span className="text-xs font-bold text-brand-accent uppercase tracking-wider block" data-id="element-123">
                        Step 1
                      </span>
                      <h3 className="text-3xl font-bold" data-id="element-124">
                        Discovery & Alignment
                      </h3>
                    </div>
                  </div>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6" data-id="element-125">
                    We start by understanding your vision, your users, and what
                    success looks like. Whether you're a founder defining your
                    MVP, a nonprofit understanding client needs, or building a
                    personal project—we help you clarify what to build and why.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-126">
                    <div className="flex items-start gap-3" data-id="element-127">
                      <CheckCircle2 className="w-5 h-5 text-brand-accent mt-1 flex-shrink-0" data-id="element-128" />
                      <div data-id="element-129">
                        <h4 className="font-semibold mb-1" data-id="element-130">
                          Define goals and success metrics
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-131">
                          What does success look like?
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-132">
                      <CheckCircle2 className="w-5 h-5 text-brand-accent mt-1 flex-shrink-0" data-id="element-133" />
                      <div data-id="element-134">
                        <h4 className="font-semibold mb-1" data-id="element-135">
                          Understand your users
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-136">
                          Who are you building for?
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-137">
                      <CheckCircle2 className="w-5 h-5 text-brand-accent mt-1 flex-shrink-0" data-id="element-138" />
                      <div data-id="element-139">
                        <h4 className="font-semibold mb-1" data-id="element-140">
                          Identify constraints
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-141">
                          Resources and timeline
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-142">
                      <CheckCircle2 className="w-5 h-5 text-brand-accent mt-1 flex-shrink-0" data-id="element-143" />
                      <div data-id="element-144">
                        <h4 className="font-semibold mb-1" data-id="element-145">
                          Spot opportunities
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-146">
                          Where can you win?
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Step 2: Design & Prototype - Left Side */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="relative pb-24 grid md:grid-cols-2 gap-8 items-center" data-id="element-147">
            {/* Content Card - Left */}
            <div className="relative md:order-1" data-id="element-148">
              <div className="bg-gradient-to-br from-brand-accent to-cyan-500 text-white p-8 md:p-10 rounded-3xl shadow-xl relative overflow-hidden" data-id="element-149">
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-3xl" data-id="element-150" />
                <div className="relative z-10" data-id="element-151">
                  <div className="flex items-center gap-3 mb-4" data-id="element-152">
                    <Sparkles className="w-8 h-8 text-brand-gold" data-id="element-153" />
                    <div data-id="element-154">
                      <span className="text-xs font-bold text-brand-gold uppercase tracking-wider block" data-id="element-155">
                        Step 2
                      </span>
                      <h3 className="text-3xl font-bold" data-id="element-156">Design & Prototype</h3>
                    </div>
                  </div>
                  <p className="text-lg text-teal-50 leading-relaxed mb-6" data-id="element-157">
                    We sketch solutions, map user flows, and build quick
                    prototypes you can see and touch. This is where ideas become
                    tangible—whether it's a clickable app prototype, a working
                    website demo, or a scrappy first version you can test with
                    real users.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-158">
                    <div className="flex items-start gap-3" data-id="element-159">
                      <CheckCircle2 className="w-5 h-5 text-brand-gold mt-1 flex-shrink-0" data-id="element-160" />
                      <div data-id="element-161">
                        <h4 className="font-semibold mb-1" data-id="element-162">
                          Sketch user flows
                        </h4>
                        <p className="text-sm text-teal-100" data-id="element-163">
                          Map the user journey
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-164">
                      <CheckCircle2 className="w-5 h-5 text-brand-gold mt-1 flex-shrink-0" data-id="element-165" />
                      <div data-id="element-166">
                        <h4 className="font-semibold mb-1" data-id="element-167">
                          Design interfaces
                        </h4>
                        <p className="text-sm text-teal-100" data-id="element-168">
                          Create visual designs
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-169">
                      <CheckCircle2 className="w-5 h-5 text-brand-gold mt-1 flex-shrink-0" data-id="element-170" />
                      <div data-id="element-171">
                        <h4 className="font-semibold mb-1" data-id="element-172">Build prototypes</h4>
                        <p className="text-sm text-teal-100" data-id="element-173">
                          Clickable, testable demos
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-174">
                      <CheckCircle2 className="w-5 h-5 text-brand-gold mt-1 flex-shrink-0" data-id="element-175" />
                      <div data-id="element-176">
                        <h4 className="font-semibold mb-1" data-id="element-177">Test and iterate</h4>
                        <p className="text-sm text-teal-100" data-id="element-178">
                          Refine with feedback
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right spacer */}
            <div className="hidden md:block md:order-2" data-id="element-179" />
          </motion.div>

          {/* Step 3: Build & Launch - Right Side */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="relative grid md:grid-cols-2 gap-8 items-center" data-id="element-180">
            {/* Left spacer */}
            <div className="hidden md:block" data-id="element-181" />

            {/* Content Card - Right */}
            <div className="relative" data-id="element-182">
              <div className="bg-gradient-to-br from-orange-600 to-amber-600 text-white p-8 md:p-10 rounded-3xl shadow-xl relative overflow-hidden" data-id="element-183">
                <div className="absolute top-0 left-0 w-32 h-32 bg-yellow-400/10 rounded-full blur-3xl" data-id="element-184" />
                <div className="relative z-10" data-id="element-185">
                  <div className="flex items-center gap-3 mb-4" data-id="element-186">
                    <Rocket className="w-8 h-8 text-yellow-200" data-id="element-187" />
                    <div data-id="element-188">
                      <span className="text-xs font-bold text-yellow-200 uppercase tracking-wider block" data-id="element-189">
                        Step 3
                      </span>
                      <h3 className="text-3xl font-bold" data-id="element-190">Build & Launch</h3>
                    </div>
                  </div>
                  <p className="text-lg text-orange-50 leading-relaxed mb-6" data-id="element-191">
                    We build the real thing—a working product you can ship.
                    Whether it's your startup's MVP, your nonprofit's client
                    app, or your personal website, we develop it, test it with
                    real users, and help you launch it into the world.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-192">
                    <div className="flex items-start gap-3" data-id="element-193">
                      <CheckCircle2 className="w-5 h-5 text-yellow-200 mt-1 flex-shrink-0" data-id="element-194" />
                      <div data-id="element-195">
                        <h4 className="font-semibold mb-1" data-id="element-196">
                          Develop the product
                        </h4>
                        <p className="text-sm text-orange-100" data-id="element-197">
                          Build it for real
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-198">
                      <CheckCircle2 className="w-5 h-5 text-yellow-200 mt-1 flex-shrink-0" data-id="element-199" />
                      <div data-id="element-200">
                        <h4 className="font-semibold mb-1" data-id="element-201">
                          Quality assurance
                        </h4>
                        <p className="text-sm text-orange-100" data-id="element-202">
                          Test thoroughly
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-203">
                      <CheckCircle2 className="w-5 h-5 text-yellow-200 mt-1 flex-shrink-0" data-id="element-204" />
                      <div data-id="element-205">
                        <h4 className="font-semibold mb-1" data-id="element-206">User validation</h4>
                        <p className="text-sm text-orange-100" data-id="element-207">
                          Test with real users
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-208">
                      <CheckCircle2 className="w-5 h-5 text-yellow-200 mt-1 flex-shrink-0" data-id="element-209" />
                      <div data-id="element-210">
                        <h4 className="font-semibold mb-1" data-id="element-211">
                          Deploy and launch
                        </h4>
                        <p className="text-sm text-orange-100" data-id="element-212">Go live!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mt-20 text-center" data-id="element-213">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md" data-id="element-214">
            <span className="text-gray-600 font-medium" data-id="element-215">
              Ready to get started?
            </span>
            <a href="#contact" className="text-brand-accent font-bold hover:text-brand-dark transition-colors flex items-center gap-1 group" data-id="element-216">
              Let's talk
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-217" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
};
const Contact = () => {
  return <section id="contact" className="py-32 bg-brand-dark relative overflow-hidden" data-id="element-218">
      <div className="absolute inset-0 bg-brand-dark" data-id="element-219">
        <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-l from-brand-accent/5 to-transparent" data-id="element-220" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10" data-id="element-221">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-222">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-223">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight" data-id="element-224">
              Let's Build <br data-id="element-225" />
              <span className="text-brand-accent" data-id="element-226">Something Together</span>
            </h2>
            <p className="text-xl text-gray-300 mb-12 font-light leading-relaxed" data-id="element-227">
              Have an idea you want to turn into a working product? Let's talk.
              We'll start with a 30-minute exploratory session to understand
              your vision and see if we're a good fit to work together.
            </p>

            <div className="flex items-center gap-4 text-white/80" data-id="element-228">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center" data-id="element-229">
                <Mail className="w-5 h-5" data-id="element-230" />
              </div>
              <div data-id="element-231">
                <p className="text-sm text-gray-400 uppercase tracking-wider" data-id="element-232">
                  Email Us
                </p>
                <a href="mailto:info@katalystproductlab.com" className="text-lg font-medium hover:text-brand-accent transition-colors" data-id="element-233">
                  info@katalystproductlab.com
                </a>
              </div>
            </div>
          </motion.div>

          <motion.form action="mailto:info@katalystproductlab.com" method="post" encType="text/plain" initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-white p-8 md:p-10 rounded-3xl shadow-2xl" data-id="element-234">
            <div className="space-y-6" data-id="element-235">
              <div className="grid md:grid-cols-2 gap-6" data-id="element-236">
                <div className="space-y-2" data-id="element-237">
                  <label className="text-sm font-semibold text-gray-700 ml-1" data-id="element-238">
                    Name
                  </label>
                  <input type="text" name="name" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all" placeholder="John Doe" required data-id="element-239" />
                </div>
                <div className="space-y-2" data-id="element-240">
                  <label className="text-sm font-semibold text-gray-700 ml-1" data-id="element-241">
                    Company
                  </label>
                  <input type="text" name="company" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all" placeholder="Acme Inc." required data-id="element-242" />
                </div>
              </div>

              <div className="space-y-2" data-id="element-243">
                <label className="text-sm font-semibold text-gray-700 ml-1" data-id="element-244">
                  Email
                </label>
                <input type="email" name="email" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all" placeholder="john@company.com" required data-id="element-245" />
              </div>

              <div className="space-y-2" data-id="element-246">
                <label className="text-sm font-semibold text-gray-700 ml-1" data-id="element-247">
                  Tell Us About Your Idea
                </label>
                <textarea name="challenge" rows={4} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all resize-none" placeholder="What are you trying to build? What stage are you at? What kind of help do you need?" required data-id="element-248"></textarea>
              </div>

              <button type="submit" className="w-full py-4 bg-brand-dark text-white font-bold rounded-xl hover:bg-brand-accent transition-colors duration-300 flex items-center justify-center gap-2 group" data-id="element-249">
                Let's Talk
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" data-id="element-250" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>;
};
const Footer = () => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <footer className="bg-gray-900 text-gray-400 py-12 border-t border-gray-800" data-id="element-251">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-252">
          <div className="grid md:grid-cols-3 gap-8 mb-8" data-id="element-253">
            <div data-id="element-254">
              <div className="flex items-center gap-3 mb-4" data-id="element-255">
                <KatalystLogo isDark={true} className="h-10 w-auto" data-id="element-256" />
                <span className="text-white font-semibold" data-id="element-257">
                  Katalyst Product Lab
                </span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed" data-id="element-258">
                Helping founders, nonprofits, and mission-driven teams turn
                ideas into working products.
              </p>
            </div>

            <div data-id="element-259">
              <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider" data-id="element-260">
                Contact
              </h3>
              <div className="space-y-2 text-sm" data-id="element-261">
                <a href="mailto:info@katalystproductlab.com" className="block hover:text-white transition-colors" data-id="element-262">
                  info@katalystproductlab.com
                </a>
              </div>
            </div>

            <div data-id="element-263">
              <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider" data-id="element-264">
                Legal
              </h3>
              <div className="space-y-2 text-sm" data-id="element-265">
                <button onClick={() => setShowPrivacy(true)} className="block hover:text-white transition-colors text-left" data-id="element-266">
                  Privacy Policy
                </button>
                <button onClick={() => setShowTerms(true)} className="block hover:text-white transition-colors text-left" data-id="element-267">
                  Terms of Service
                </button>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-600" data-id="element-268">
            <p data-id="element-269">&copy; 2024 Katalyst Product Lab. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Privacy and Terms modals remain the same */}
      <AnimatePresence data-id="element-270">
        {showPrivacy && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPrivacy(false)} data-id="element-271">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-8 md:p-12" onClick={e => e.stopPropagation()} data-id="element-272">
              <div className="flex justify-between items-start mb-6" data-id="element-273">
                <h2 className="text-3xl font-bold text-brand-dark" data-id="element-274">
                  Privacy Policy
                </h2>
                <button onClick={() => setShowPrivacy(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-275">
                  <X className="w-6 h-6" data-id="element-276" />
                </button>
              </div>

              <div className="prose prose-gray max-w-none space-y-6 text-gray-700" data-id="element-277">
                <p className="text-sm text-gray-500" data-id="element-278">
                  Last updated: {new Date().toLocaleDateString()}
                </p>

                <section data-id="element-279">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-280">
                    Introduction
                  </h3>
                  <p data-id="element-281">
                    Katalyst Product Lab ("we," "our," or "us") is committed to
                    protecting your privacy. This Privacy Policy explains how we
                    collect, use, disclose, and safeguard your information when
                    you visit our website or use our services.
                  </p>
                </section>

                <section data-id="element-282">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-283">
                    Information We Collect
                  </h3>
                  <p data-id="element-284">
                    We collect information that you provide directly to us,
                    including:
                  </p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-285">
                    <li data-id="element-286">
                      Name and contact information (email address, phone number)
                    </li>
                    <li data-id="element-287">Company name and professional information</li>
                    <li data-id="element-288">
                      Information about your product challenges and business
                      needs
                    </li>
                    <li data-id="element-289">Communications you send to us</li>
                  </ul>
                </section>

                <section data-id="element-290">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-291">
                    How We Use Your Information
                  </h3>
                  <p data-id="element-292">We use the information we collect to:</p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-293">
                    <li data-id="element-294">Provide, maintain, and improve our services</li>
                    <li data-id="element-295">
                      Respond to your inquiries and provide customer support
                    </li>
                    <li data-id="element-296">Send you relevant information about our services</li>
                    <li data-id="element-297">Analyze usage patterns and improve user experience</li>
                    <li data-id="element-298">Comply with legal obligations</li>
                  </ul>
                </section>

                <section data-id="element-299">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-300">
                    Information Sharing
                  </h3>
                  <p data-id="element-301">
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share your information only in the
                    following circumstances:
                  </p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-302">
                    <li data-id="element-303">With your consent</li>
                    <li data-id="element-304">To comply with legal obligations</li>
                    <li data-id="element-305">To protect our rights and prevent fraud</li>
                    <li data-id="element-306">
                      With service providers who assist in our operations (under
                      strict confidentiality agreements)
                    </li>
                  </ul>
                </section>

                <section data-id="element-307">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-308">
                    Data Security
                  </h3>
                  <p data-id="element-309">
                    We implement appropriate technical and organizational
                    measures to protect your personal information against
                    unauthorized access, alteration, disclosure, or destruction.
                  </p>
                </section>

                <section data-id="element-310">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-311">
                    Your Rights
                  </h3>
                  <p data-id="element-312">You have the right to:</p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-313">
                    <li data-id="element-314">Access the personal information we hold about you</li>
                    <li data-id="element-315">Request correction of inaccurate information</li>
                    <li data-id="element-316">Request deletion of your information</li>
                    <li data-id="element-317">Opt-out of marketing communications</li>
                  </ul>
                </section>

                <section data-id="element-318">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-319">
                    Contact Us
                  </h3>
                  <p data-id="element-320">
                    If you have questions about this Privacy Policy, please
                    contact us at:
                  </p>
                  <p className="font-medium" data-id="element-321">
                    Email:{' '}
                    <a href="mailto:info@katalystproductlab.com" className="text-brand-accent hover:underline" data-id="element-322">
                      info@katalystproductlab.com
                    </a>
                  </p>
                </section>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-323">
        {showTerms && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowTerms(false)} data-id="element-324">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-8 md:p-12" onClick={e => e.stopPropagation()} data-id="element-325">
              <div className="flex justify-between items-start mb-6" data-id="element-326">
                <h2 className="text-3xl font-bold text-brand-dark" data-id="element-327">
                  Terms of Service
                </h2>
                <button onClick={() => setShowTerms(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-328">
                  <X className="w-6 h-6" data-id="element-329" />
                </button>
              </div>

              <div className="prose prose-gray max-w-none space-y-6 text-gray-700" data-id="element-330">
                <p className="text-sm text-gray-500" data-id="element-331">
                  Last updated: {new Date().toLocaleDateString()}
                </p>

                <section data-id="element-332">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-333">
                    Agreement to Terms
                  </h3>
                  <p data-id="element-334">
                    By accessing or using Katalyst Product Lab's services, you
                    agree to be bound by these Terms of Service. If you disagree
                    with any part of these terms, you may not access our
                    services.
                  </p>
                </section>

                <section data-id="element-335">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-336">
                    Services Description
                  </h3>
                  <p data-id="element-337">
                    Katalyst Product Lab provides product innovation consulting,
                    strategic transformation services, and go-to-market
                    acceleration services. The specific scope of services will
                    be defined in individual service agreements.
                  </p>
                </section>

                <section data-id="element-338">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-339">
                    Client Responsibilities
                  </h3>
                  <p data-id="element-340">Clients agree to:</p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-341">
                    <li data-id="element-342">Provide accurate and complete information</li>
                    <li data-id="element-343">Cooperate in good faith throughout the engagement</li>
                    <li data-id="element-344">Provide timely feedback and approvals</li>
                    <li data-id="element-345">Respect intellectual property rights</li>
                  </ul>
                </section>

                <section data-id="element-346">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-347">
                    Intellectual Property
                  </h3>
                  <p data-id="element-348">
                    All methodologies, frameworks, and proprietary processes
                    developed by Katalyst Product Lab remain our intellectual
                    property. Client-specific deliverables and work product will
                    be governed by individual service agreements.
                  </p>
                </section>

                <section data-id="element-349">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-350">
                    Confidentiality
                  </h3>
                  <p data-id="element-351">
                    We maintain strict confidentiality regarding all client
                    information and business matters. Both parties agree to
                    protect confidential information disclosed during the
                    engagement.
                  </p>
                </section>

                <section data-id="element-352">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-353">
                    Limitation of Liability
                  </h3>
                  <p data-id="element-354">
                    Katalyst Product Lab provides consulting services and
                    strategic guidance. While we strive for excellence, we
                    cannot guarantee specific business outcomes. Our liability
                    is limited to the fees paid for services rendered.
                  </p>
                </section>

                <section data-id="element-355">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-356">
                    Termination
                  </h3>
                  <p data-id="element-357">
                    Either party may terminate services with written notice as
                    specified in the service agreement. Termination does not
                    relieve either party of obligations incurred prior to
                    termination.
                  </p>
                </section>

                <section data-id="element-358">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-359">
                    Governing Law
                  </h3>
                  <p data-id="element-360">
                    These terms shall be governed by and construed in accordance
                    with applicable laws, without regard to conflict of law
                    provisions.
                  </p>
                </section>

                <section data-id="element-361">
                  <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-362">
                    Contact Information
                  </h3>
                  <p data-id="element-363">
                    For questions about these Terms of Service, please contact
                    us at:
                  </p>
                  <p className="font-medium" data-id="element-364">
                    Email:{' '}
                    <a href="mailto:info@katalystproductlab.com" className="text-brand-accent hover:underline" data-id="element-365">
                      info@katalystproductlab.com
                    </a>
                  </p>
                </section>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};
export function App() {
  return <div className="min-h-screen bg-white" data-id="element-366">
      <Navbar data-id="element-367" />
      <Hero data-id="element-368" />
      <Expertise data-id="element-369" />
      <Method data-id="element-370" />
      <Philosophy data-id="element-371" />
      <Contact data-id="element-372" />
      <Footer data-id="element-373" />
    </div>;
}